import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { finalize, timeout } from 'rxjs/operators';
import packageInfo from '../../../../package.json';

interface WebVersionManifest {
  version?: unknown;
}

export type VersionGateKind = 'loading' | 'hidden' | 'required';

export interface VersionGateState {
  kind: VersionGateKind;
  currentVersion?: string;
  latestVersion?: string;
}

const SEMANTIC_VERSION = /^\d+\.\d+\.\d+$/;

export function compareVersions(left: string, right: string): number {
  if (!SEMANTIC_VERSION.test(left) || !SEMANTIC_VERSION.test(right)) {
    throw new Error('Versions must use major.minor.patch format');
  }

  const leftParts = left.split('.').map(Number);
  const rightParts = right.split('.').map(Number);
  for (let index = 0; index < leftParts.length; index += 1) {
    if (leftParts[index] !== rightParts[index]) {
      return leftParts[index] < rightParts[index] ? -1 : 1;
    }
  }
  return 0;
}

export function buildReloadUrl(currentUrl: string, version: string): string {
  const url = new URL(currentUrl);
  url.searchParams.set('__wallit_version', version);
  return url.toString();
}

@Injectable({ providedIn: 'root' })
export class VersionPolicyService implements OnDestroy {
  readonly state = new BehaviorSubject<VersionGateState>({ kind: 'loading' });

  private initialized = false;
  private requestInFlight = false;
  private readonly visibilityChangeHandler = (): void => {
    if (document.visibilityState === 'visible') {
      this.check();
    }
  };

  constructor(private http: HttpClient) {}

  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.check();
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  check(): void {
    if (this.requestInFlight || this.state.value.kind === 'required') {
      return;
    }

    this.state.next({ kind: 'loading' });
    this.requestInFlight = true;
    this.http.get<WebVersionManifest>(`/version.json?v=${Date.now()}`)
      .pipe(
        timeout(10000),
        finalize(() => { this.requestInFlight = false; }),
      )
      .subscribe({
        next: manifest => this.evaluate(manifest),
        error: error => {
          console.error('Unable to load the web version manifest', error);
          this.state.next({ kind: 'hidden' });
        },
      });
  }

  reload(): void {
    window.location.replace(buildReloadUrl(
      window.location.href,
      this.state.value.latestVersion || Date.now().toString(),
    ));
  }

  private evaluate(manifest: WebVersionManifest): void {
    try {
      if (!manifest
        || typeof manifest.version !== 'string'
        || !manifest.version.trim()) {
        throw new Error('Web version manifest is invalid');
      }

      const currentVersion = packageInfo.version;
      const latestVersion = manifest.version;
      if (compareVersions(currentVersion, latestVersion) >= 0) {
        this.state.next({ kind: 'hidden' });
        return;
      }

      this.state.next({
        kind: 'required',
        currentVersion,
        latestVersion,
      });
    } catch (error) {
      console.error('Unable to validate the web version manifest', error);
      this.state.next({ kind: 'hidden' });
    }
  }
}
